import {
  Badge,
  Alert,
  Button,
  Dialog,
  isRefusedLink,
  RouterProvider as LinkRouter,
  linkHref,
  Shell,
  ToastRegion,
  showToast,
} from "@emdur/ui";
import { useEffect, useMemo, useState } from "react";
import {
  Outlet,
  useHref,
  useLocation,
  useMatch,
  useNavigate,
} from "react-router";
import { useApi } from "../lib/apiContext";
import { CommandPalette } from "./CommandPalette";
import { ShowOrganization, type ShownOrganization } from "./currentOrganization";
import { RegistryProvider, useRegistry } from "./RegistryProvider";
import { signOut } from "../lib/auth";
import { PermissionRefreshAction } from "./PermissionRefreshAction";
import { errorText } from "../lib/errors";

/** Every page's frame: the kit's Shell (skip link, the emdur wordmark as the way home), the kit's links routed by
 * react-router, and the toasts. The brand editor gets the wide frame (its preview sits beside the controls). */
export function ConsoleShell() {
  return (
    <RegistryProvider>
      <ConsoleFrame />
    </RegistryProvider>
  );
}
function ConsoleFrame() {
  const navigate = useNavigate();
  const { apiUrl } = useApi();
  const links = useMemo(() => consoleLinks(apiUrl), [apiUrl]);
  const location = useLocation();
  const registry = useRegistry();
  const [palette, setPalette] = useState(false);
  const [help, setHelp] = useState(false);
  const [permissionsWidened, setPermissionsWidened] = useState(false);
  const [signingOut, setSigningOut] = useState(false);
  const [accountError, setAccountError] = useState("");
  const publicPage = /^(\/signin|\/callback|\/invitations)$/.test(
    location.pathname,
  );
  const currentOrg = location.pathname.match(/^\/orgs\/([^/]+)/)?.[1];
  const [shown, setShown] = useState<ShownOrganization>(null);
  const closedHere = !!currentOrg && shown?.id === currentOrg && shown.state === "inactive";
  useEffect(() => {
    const widened = () => setPermissionsWidened(true);
    window.addEventListener("emdur.permissions-widened", widened);
    return () =>
      window.removeEventListener("emdur.permissions-widened", widened);
  }, []);
  async function leave() {
    if (signingOut) return;
    setSigningOut(true);
    try {
      const revoked = await signOut();
      if (!revoked)
        sessionStorage.setItem(
          "emdur.signout",
          "You signed out on this device. The server did not confirm that the session ended. Sign in and try again when you're online.",
        );
      window.location.replace("/signin");
    } catch (failure) {
      setAccountError(errorText(failure));
      setSigningOut(false);
    }
  }
  const wide =
    useMatch("/orgs/:id/brands/:brandId") !== null ||
    location.pathname.includes("/apps") ||
    location.pathname.includes("/agents") ||
    location.pathname.includes("/roles") ||
    location.pathname.startsWith("/approvals");
  useEffect(() => {
    const shortcut = (event: KeyboardEvent) => {
      if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === "k") {
        event.preventDefault();
        setPalette((open) => !open);
      }
    };
    document.addEventListener("keydown", shortcut);
    return () => document.removeEventListener("keydown", shortcut);
  }, []);
  return (
    <LinkRouter
      navigate={(to) => {
        if (!links.isRefused(to))
          void navigate(to, {
            viewTransition:
              typeof document.startViewTransition === "function" &&
              ["/apps", "/agents", "/roles"].some(
                (path) => to.includes(path) || location.pathname.includes(path),
              ) &&
              !window.matchMedia?.("(prefers-reduced-motion: reduce)").matches,
          });
      }}
      useHref={links.useLinkHref}
    >
      <div
        className="console-frame relative"
        data-account={(!publicPage && !!registry.personId) || undefined}
      >
        <Shell
          wordmark={<Wordmark />}
          homeHref="/"
          width={wide ? "wide" : "narrow"}
          account={
            !publicPage && registry.personId
              ? {
                  label: "Account",
                  trigger: (
                    <span
                      className="block max-w-16 truncate md:max-w-24"
                      title={registry.personName}
                    >
                      {registry.personName || "Account"}
                    </span>
                  ),
                  items: [
                    ...(!registry.loading &&
                    !registry.error &&
                    registry.proposals.length
                      ? [
                          {
                            id: "pending-approvals",
                            label: `${registry.proposals.length} pending approvals`,
                            href: "/approvals",
                          },
                        ]
                      : []),
                    {
                      id: "signout",
                      label: signingOut ? "Signing out…" : "Sign out",
                      isDisabled: signingOut,
                      onAction: () => void leave(),
                    },
                  ],
                }
              : undefined
          }
          navigation={[
            {
              href: "/",
              label: "Organisations",
              isCurrent: location.pathname === "/",
            },
            {
              href: "/approvals",
              label: "Approvals",
              isCurrent: location.pathname.startsWith("/approvals"),
            },
            // A deactivated organisation's areas answer Not found: while one is shown, offer none of them.
            ...(closedHere
              ? []
              : [
                  {
                    href: currentOrg ? `/orgs/${currentOrg}/agents` : "/agents",
                    label: "Agents",
                    isCurrent: location.pathname.includes("/agents"),
                  },
                  {
                    href: currentOrg ? `/orgs/${currentOrg}/roles` : "/roles",
                    label: "Roles",
                    isCurrent: location.pathname.includes("/roles"),
                  },
                ]),
          ]}
          navigationLabel="Console"
        >
          <div className="console-tools pointer-events-none absolute inset-x-0 top-3 z-10 mx-auto flex max-w-6xl justify-end gap-2 px-4">
            <div className="pointer-events-auto flex items-center gap-2">
              {!registry.loading &&
                !registry.error &&
                registry.proposals.length > 0 && (
                  <span className="hidden md:inline-flex">
                    <Badge tone="accent">
                      {registry.proposals.length} pending
                    </Badge>
                  </span>
                )}
              <Button
                aria-label="Search… ⌘ / Ctrl K"
                aria-keyshortcuts="Meta+K Control+K"
                onPress={() => setPalette(true)}
                icon={
                  <svg
                    aria-hidden="true"
                    viewBox="0 0 24 24"
                    className="size-4"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                  >
                    <circle cx="10" cy="10" r="6" />
                    <path d="m15 15 5 5" />
                  </svg>
                }
              >
                <span className="console-search-label">
                  Search…{" "}
                  <kbd className="ms-4 text-sm text-muted">⌘ / Ctrl K</kbd>
                </span>
              </Button>
              <Button
                aria-label="Help"
                variant="quiet"
                onPress={() => setHelp(true)}
              >
                ?
              </Button>
            </div>
          </div>
          {!publicPage && permissionsWidened && (
            <Alert title="Sign in again to refresh permissions" tone="warning">
              <p>
                Your current sign-in does not include the permission needed
                here. Verify with your passkey to request your latest access.
              </p>
              <PermissionRefreshAction />
            </Alert>
          )}
          {accountError && <Alert tone="danger">{accountError}</Alert>}
          <ShowOrganization value={setShown}>
            <Outlet />
          </ShowOrganization>
        </Shell>
      </div>
      <ToastRegion />
      <CommandPalette isOpen={palette} onOpenChange={setPalette} />
      <Dialog title="Help" isOpen={help} onOpenChange={setHelp}>
        <div className="flex flex-col gap-4">
          <p>
            Manage your apps' permissions and review changes proposed by agents.
            If a manifest is refused, ask the app's team for a corrected
            manifest.
          </p>
          <p>
            Contact Foundation support:{" "}
            <span className="font-strong">api@emdur.com</span>
          </p>
          <p className="text-sm text-muted">
            Include the app's name and what you were trying to do. Keep private
            keys and sign-in credentials out of your message.
          </p>
          <Button
            onPress={async () => {
              try {
                await navigator.clipboard.writeText("api@emdur.com");
                showToast({
                  title: "Support address copied",
                  tone: "positive",
                });
              } catch {
                showToast({
                  title: "Select the support address to copy it.",
                  tone: "warning",
                });
              }
            }}
          >
            Copy support address
          </Button>
        </div>
      </Dialog>
    </LinkRouter>
  );
}

/** The kit's link rule for the console: its own paths through react-router; the one other origin allowed is the
 * Foundation API's (config.json's api_url), whose public files hold a brand version's theme.css and tokens.json.
 * Anything else (another site, `//host`, `javascript:` …) renders as "#" and never navigates. Made once per API
 * address (it never changes while the console runs), so the hook React Aria calls keeps one identity. */
function consoleLinks(apiUrl: string) {
  const options = { allowOrigins: [new URL(apiUrl).origin] };
  return {
    useLinkHref: linkHref(useHref, options),
    isRefused: (to: string) => isRefusedLink(to, options),
  };
}

/** The lowercase emdur wordmark (BRAND.md), white on a dark device, and the console's name. */
function Wordmark() {
  return (
    <span className="inline-flex items-center gap-3">
      <picture>
        <source
          srcSet="/brand/emdur-wordmark-white.svg"
          media="(prefers-color-scheme: dark)"
        />
        <img
          src="/brand/emdur-wordmark-colour.svg"
          alt="emdur"
          className="h-6 w-auto"
        />
      </picture>{" "}
      <span className="console-wordmark-label text-muted">console</span>
    </span>
  );
}
